/**
 * Navigation Bar Component - Grras Solutions Style
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../services/api';

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    logout();
  };

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="sticky top-0 z-[9999] bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 border-b border-blue-800/50 shadow-xl backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Beautiful Brand with Icon */}
          <Link 
            to="/dashboard" 
            className="flex items-center space-x-3 group transition-all duration-300 hover:scale-105"
            data-testid="navbar-logo"
          >
            <img 
              src="/images/grras-logo.png"
              alt="Grras Solutions" 
              className="h-14 md:h-16 w-auto"
            />
          </Link>

          {/* Beautiful Navigation Links */}
          <div className="hidden md:flex items-center space-x-1 lg:space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 border border-white/20">
            <Link
              to="/dashboard"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                isActive('/dashboard') 
                  ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/25' 
                  : 'text-blue-100 hover:bg-white/10 hover:text-white'
              }`}
              data-testid="navbar-dashboard"
            >
              📊 Dashboard
            </Link>
            
            <Link
              to="/courses"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                isActive('/courses') 
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25' 
                  : 'text-blue-100 hover:bg-white/10 hover:text-white'
              }`}
              data-testid="navbar-courses"
            >
              🎓 Courses
            </Link>
            
            <Link
              to="/mock-tests"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                isActive('/mock-tests') 
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg shadow-purple-500/25' 
                  : 'text-blue-100 hover:bg-white/10 hover:text-white'
              }`}
              data-testid="navbar-mock-tests"
            >
              📝 Mock Tests
            </Link>
            
            <Link
              to="/pricing"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                isActive('/pricing') 
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/25' 
                  : 'text-blue-100 hover:bg-white/10 hover:text-white'
              }`}
              data-testid="navbar-pricing"
            >
              💎 Pricing
            </Link>
            
            <Link
              to="/projects"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                isActive('/projects') 
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/25' 
                  : 'text-blue-100 hover:bg-white/10 hover:text-white'
              }`}
              data-testid="navbar-projects"
            >
              🛠 Projects
            </Link>

            <Link
              to="/leaderboard"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                isActive('/leaderboard')
                  ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-lg shadow-amber-500/25'
                  : 'text-blue-100 hover:bg-white/10 hover:text-white'
              }`}
              data-testid="navbar-leaderboard"
            >
              🏆 Leaderboard
            </Link>

            <Link
              to="/jobs"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                isActive('/jobs')
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/25'
                  : 'text-blue-100 hover:bg-white/10 hover:text-white'
              }`}
              data-testid="navbar-jobs"
            >
              💼 Jobs
            </Link>
            
            <Link
              to="/help-center"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                isActive('/help-center') 
                  ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/25' 
                  : 'text-blue-100 hover:bg-white/10 hover:text-white'
              }`}
              data-testid="navbar-help-center"
            >
              🎧 Help
            </Link>
          </div>

          {/* Mobile Menu Button - Enhanced */}
          <div className="md:hidden">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-2 text-blue-100 hover:bg-white/20 transition-all duration-300"
              aria-label="Toggle mobile menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Avatar dropdown */}
          <div className="relative z-[9999]" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen((v) => !v)}
              className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm rounded-full px-3 py-2 border border-white/20 hover:bg-white/20 transition-all duration-200"
              aria-haspopup="true"
              aria-expanded={dropdownOpen}
              data-testid="avatar-menu-button"
            >
              {user?.avatar_url ? (
                <img
                  src={`${API_BASE}${user.avatar_url}`}
                  alt="avatar"
                  className="w-7 h-7 rounded-full object-cover"
                />
              ) : (
                <div className="bg-gradient-to-r from-emerald-400 to-cyan-400 p-1.5 rounded-full">
                  <span className="text-xs text-white font-bold">
                    {(user?.first_name || 'U').charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              {/* Caret */}
              <svg
                className={`w-3 h-3 text-blue-200 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 min-w-max bg-slate-900 border border-blue-800/50 rounded-xl shadow-2xl shadow-black/50 z-[9999] overflow-hidden">
                {/* User info header — display only */}
                <div className="px-4 py-3 border-b border-slate-700/60">
                  <p className="text-sm font-semibold text-white truncate">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-slate-400 truncate mt-0.5">{user?.email || ''}</p>
                </div>

                {/* Profile */}
                <Link
                  to="/profile"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-blue-100 hover:bg-white/10 transition-colors"
                  data-testid="dropdown-profile"
                >
                  <span>👤</span> Profile
                </Link>

                {/* Progress */}
                <Link
                  to="/progress"
                  onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-blue-100 hover:bg-white/10 transition-colors"
                  data-testid="dropdown-progress"
                >
                  <span>📈</span> My Progress
                </Link>

                {/* Logout */}
                <button
                  onClick={() => { handleLogout(); setDropdownOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-300 hover:bg-red-500/10 transition-colors border-t border-slate-700/60"
                  data-testid="logout-button"
                >
                  <span>🚪</span> Logout
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Navigation Menu - Enhanced */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-900/95 backdrop-blur-sm border-t border-blue-800/50">
            <div className="px-4 py-3 space-y-2">
              <Link
                to="/dashboard"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/dashboard'
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    : 'text-blue-100 hover:bg-white/10'
                }`}
              >
                📊 Dashboard
              </Link>
              <Link
                to="/courses"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/courses'
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    : 'text-blue-100 hover:bg-white/10'
                }`}
              >
                📚 Courses
              </Link>
              <Link
                to="/progress"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/progress'
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    : 'text-blue-100 hover:bg-white/10'
                }`}
              >
                📈 Progress
              </Link>
              <Link
                to="/mock-tests"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/mock-tests'
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    : 'text-blue-100 hover:bg-white/10'
                }`}
              >
                🧪 Mock Tests
              </Link>
              <Link
                to="/pricing"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/pricing'
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                    : 'text-blue-100 hover:bg-white/10'
                }`}
              >
                💎 Pricing
              </Link>
              <Link
                to="/projects"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/projects'
                    ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                    : 'text-blue-100 hover:bg-white/10'
                }`}
              >
                🛠 Projects
              </Link>
              <Link
                to="/leaderboard"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/leaderboard'
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                    : 'text-blue-100 hover:bg-white/10'
                }`}
              >
                🏆 Leaderboard
              </Link>
              <Link
                to="/help-center"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/help-center'
                    ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                    : 'text-blue-100 hover:bg-white/10'
                }`}
              >
                🎧 Help Center
              </Link>
              
              {/* Mobile User Info */}
              <div className="border-t border-blue-800/50 pt-3 mt-3">
                <div className="px-4 py-2 text-blue-200 text-sm flex items-center gap-1 min-w-0">
                  <span className="truncate">Welcome, {user?.first_name || 'User'}</span>
                </div>
                <Link
                  to="/profile"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    location.pathname === '/profile'
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                      : 'text-blue-100 hover:bg-white/10'
                  }`}
                >
                  👤 Profile
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 rounded-lg bg-red-500/20 text-red-300 text-sm font-medium hover:bg-red-500/30 transition-all duration-200"
                >
                  🚪 Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;